import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import Skeleton from '../../../../atoms/Skeleton'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import DDPWealthJourneyEntryListItem from './DDPWealthJourneyEntryListItem'
import DDPWealthJourneyEntryListStateProvider from './DDPWealthJourneyEntryListContext'
import YearPicker from './YearPicker'

dayjs.extend(utc)

const useStyles = makeStyles((theme) => ({
  columnList: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    paddingBottom: '20px',
    position: 'relative',
    '& .wj-entry-container': {
      padding: 'var(--dim-4) var(--dim-8)',
      transition: 'background-color .2s ease-in-out',
      '&.__yearSelected': {
        backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 20%, transparent)'
      }
    }
  },
  rowList: {
    display: 'flex',
    flexDirection: 'row',
    overflowX: 'auto',
    padding: 'var(--dim-12) var(--dim-16)',

    '& .wj-entry-container': {
      padding: 'var(--dim-4)',
      transition: 'background-color .2s ease-in-out',
      '&.__yearSelected': {
        backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 25%, transparent)'
      }
    }
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--dim-12)',
    color: 'var(--color-neutral-500)',
    fontSize: '1rem'
  }
}))

const DDPWealthJourneyEntryList = ({
  pipeline,
  variables,
  dataAccessor = 'data',
  itemKeyAccessor = 'entryId',
  listDirection = 'row',
  variant = 'card',
  targetPrefix = 'Target:',
  className,
  style,
  itemProps,
  dateAccessor,
  autoSelect = true,
  selectedYear: _selectedYear,
  disableYearHighlight = false,
  showYearPicker = true,
  yearPickerProps,
  children
}) => {
  const classes = useStyles()
  const listRef = useRef(null)
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [selectedYear, setSelectedYear] = useState(_selectedYear)

  // Fetch data from pipeline
  const { data, isLoading, isFetching, error } = usePipelineDataContextWithVariables(
    pipeline,
    variables
  )

  const entries = useMemo(() => {
    return (get(data, dataAccessor) || [])?.map(entry => {
      if (entry.year) return entry
      const dateValue = dateAccessor ? get(entry, dateAccessor) : entry.entryDate
      const year = dayjs.utc(dateValue).year()

      return {
        ...entry,
        year,
        key: get(entry, itemKeyAccessor)
      }
    })
  }, [data, dataAccessor, dateAccessor, itemKeyAccessor])

  const entryYears = useMemo(() => {
    if (!showYearPicker) return []
    const yearsSet = new Set()
    entries.forEach(entry => {
      if (entry.year) {
        yearsSet.add(entry.year)
      }
    })
    return Array.from(yearsSet).sort((a, b) => a - b)
  }, [entries, showYearPicker])

  // Handle auto-selection
  useEffect(() => {
    if (!autoSelect || !entries.length) return

    const currentSelected = entries.find(e => e.key === selectedEntry?.key)

    if (currentSelected) {
      // Update selectedEntry to the new entry object (with fresh data after refetch)
      setSelectedEntry(currentSelected)
    } else {
      // No matching entry found, select first/last
      const entryToSelect = listDirection === 'row' ? entries[entries.length - 1] : entries[0]
      setSelectedEntry(entryToSelect)
      if (entryToSelect.year) {
        setSelectedYear(entryToSelect.year)
      }

      // Scroll to selected entry
      setTimeout(() => {
        const el = listRef.current?.querySelector(`[data-entry-id="${entryToSelect.key}"]`)
        if (el) {
          el.scrollIntoView({ behavior: listDirection === 'row' ? 'instant' : 'smooth', block: 'center', inline: 'center' })
        }
      }, 100)
    }
  }, [autoSelect, entries, listDirection, selectedEntry?.key])

  // Handle entry click
  const handleEntryClick = useCallback((entry) => {
    setSelectedEntry(entry)
    if (entry.year) {
      setSelectedYear(entry.year)
    }
    const el = listRef.current?.querySelector(`[data-entry-id="${entry.key}"]`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
    }
  }, [])

  if (isLoading || isFetching) {
    return (
      <div className={className} style={style}>
        <Skeleton height='400px' width='100%' />
      </div>
    )
  }

  if (error) {
    return (
      <div className={clsx(classes.emptyState, className)} style={style}>
        Error loading entries
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div className={clsx(classes.emptyState, className)} style={style}>
        No entries found
      </div>
    )
  }

  const listClass = listDirection === 'row' ? classes.rowList : classes.columnList
  return (
    <DDPWealthJourneyEntryListStateProvider
      selectedEntry={selectedEntry}
      setSelectedEntry={setSelectedEntry}
      selectedYear={selectedYear}
      setSelectedYear={setSelectedYear}
    >
      {showYearPicker && <YearPicker years={entryYears} />}
      <FadeIn>
        <div ref={listRef} className={clsx(listClass, className)} style={style}>
          {entries.map((entry) => {
            const isSelected = !!selectedEntry?.key && selectedEntry.key === entry.key
            const isYearSelected = selectedYear === entry.year

            return (
              <FadeIn
                key={entry.key}
                className={clsx('wj-entry-container', {
                  __yearSelected: !disableYearHighlight && isYearSelected
                })}
                data-year={entry.year}
              >
                <DDPWealthJourneyEntryListItem
                  entry={entry}
                  variant={variant}
                  targetPrefix={targetPrefix}
                  selected={isSelected}
                  onClick={handleEntryClick}
                  {...itemProps}
                />
              </FadeIn>
            )
          })}
        </div>
      </FadeIn>
      {children}
    </DDPWealthJourneyEntryListStateProvider>
  )
}

DDPWealthJourneyEntryList.propTypes = {
  pipeline: PropTypes.string,
  variables: PropTypes.object,
  dataAccessor: PropTypes.string,
  itemKeyAccessor: PropTypes.string,
  listDirection: PropTypes.oneOf(['row', 'column']),
  variant: PropTypes.oneOf(['card', 'horizontal-card', 'horizontal-compact']),
  targetPrefix: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  itemProps: PropTypes.object,
  dateAccessor: PropTypes.string,
  autoSelect: PropTypes.bool,
  selectedYear: PropTypes.number,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  filterFn: PropTypes.func,
  disableYearHighlight: PropTypes.bool,
  showYearPicker: PropTypes.bool,
  yearPickerProps: PropTypes.object,
  children: PropTypes.node
}

export default DDPWealthJourneyEntryList
